import { Copy, MessageSquarePlus, Trash2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { ANNOTATE, useCan } from "@/lib/access";

/**
 * Разметка экрана: клик по элементу — комментарий — текст в буфер.
 *
 * БЕЗ СЕРВЕРА И БЕЗ ЗАВИСИМОСТЕЙ, и это главное решение. Готовые аннотаторы
 * (stagewise, plannotator) живут в паре «локальный dev-сервер + агент на той же
 * машине»: они шлют комментарий агенту сами. Здесь кабинет боевой, а сессия
 * Claude — на сервере, и такой пары нет. Значит переносит комментарии человек,
 * а инструмент обязан отдать их ОДНИМ куском текста, готовым к вставке.
 *
 * Комментарии лежат в localStorage браузера: перезагрузка страницы посреди
 * разметки не должна стирать десять уже написанных замечаний. Никуда, кроме
 * буфера обмена, они не уходят.
 *
 * Адрес элемента собирается по `data-annot` вверх по дереву — отсюда
 * «таблица › строка 265194647 › дек» вместо `div.px-2.text-right`, по которому
 * не найти ни строку, ни колонку.
 */

type Note = {
  id: number;
  page: string;
  anchor: string;
  text: string;
  comment: string;
};

const STORE = "prokat.annotations";

function load(): Note[] {
  try {
    const raw = localStorage.getItem(STORE);
    return raw ? (JSON.parse(raw) as Note[]) : [];
  } catch {
    // Приватное окно, запрет на site data — разметка обязана работать и так,
    // просто не переживая перезагрузку.
    return [];
  }
}

function save(notes: Note[]): void {
  try {
    localStorage.setItem(STORE, JSON.stringify(notes));
  } catch {
    /* см. load(): молча живём без памяти */
  }
}

/** Путь к элементу через ближайшие `data-annot`; без них — тег с классами. */
function anchorOf(el: HTMLElement): string {
  const parts: string[] = [];
  let node: HTMLElement | null = el;
  while (node !== null && parts.length < 3) {
    const mark = node.dataset?.["annot"];
    if (mark !== undefined && mark !== "") parts.unshift(mark);
    node = node.parentElement;
  }
  if (parts.length > 0) return parts.join(" › ");
  const cls = (el.getAttribute("class") ?? "")
    .split(/\s+/)
    .slice(0, 2)
    .join(".");
  return el.tagName.toLowerCase() + (cls ? `.${cls}` : "");
}

/** Что на элементе написано — по этому его узнают глазами. */
function textOf(el: HTMLElement): string {
  return (el.innerText ?? "").replace(/\s+/g, " ").trim().slice(0, 80);
}

function asClipboard(notes: Note[]): string {
  const stamp = new Date().toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  const lines = [`Разметка prokat · ${stamp}`, ""];
  let page = "";
  notes.forEach((n, i) => {
    if (n.page !== page) {
      page = n.page;
      lines.push(`${window.location.origin}${page}`);
    }
    lines.push(`${i + 1}. [${n.anchor}] «${n.text}» → ${n.comment}`);
  });
  return lines.join("\n");
}

/** Разметка — по праву `annotate`: без него кнопки на экране нет вовсе. */
export function Annotator() {
  return useCan(ANNOTATE) ? <Panel /> : null;
}

function Panel() {
  const [on, setOn] = useState(false);
  const [notes, setNotes] = useState<Note[]>(load);
  const [hover, setHover] = useState<DOMRect | null>(null);
  const [target, setTarget] = useState<{
    rect: DOMRect;
    anchor: string;
    text: string;
  } | null>(null);
  const [draft, setDraft] = useState("");
  const [copied, setCopied] = useState(false);
  // Своя панель не должна попадать под прицел: клик по её кнопкам обязан
  // работать кнопкой, а не становиться замечанием о самой панели.
  const own = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLTextAreaElement>(null);

  useEffect(() => save(notes), [notes]);
  useEffect(() => {
    if (target !== null) field.current?.focus();
  }, [target]);

  useEffect(() => {
    if (!on) return;
    const mine = (el: EventTarget | null) =>
      el instanceof Node && own.current !== null && own.current.contains(el);

    const move = (e: MouseEvent) => {
      if (target !== null || mine(e.target)) return;
      setHover((e.target as HTMLElement).getBoundingClientRect());
    };
    const click = (e: MouseEvent) => {
      if (mine(e.target)) return;
      // Перехват в фазе захвата: иначе клик по строке таблицы сначала сработал
      // бы кнопкой и увёл со страницы, которую размечают.
      e.preventDefault();
      e.stopPropagation();
      const el = e.target as HTMLElement;
      setTarget({
        rect: el.getBoundingClientRect(),
        anchor: anchorOf(el),
        text: textOf(el),
      });
      setDraft("");
    };
    const key = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (target !== null) setTarget(null);
      else setOn(false);
    };

    document.addEventListener("mousemove", move, true);
    document.addEventListener("click", click, true);
    document.addEventListener("keydown", key, true);
    return () => {
      document.removeEventListener("mousemove", move, true);
      document.removeEventListener("click", click, true);
      document.removeEventListener("keydown", key, true);
    };
  }, [on, target]);

  const add = () => {
    if (target === null || draft.trim() === "") return;
    setNotes([
      ...notes,
      {
        id: Date.now(),
        page: window.location.pathname + window.location.search,
        anchor: target.anchor,
        text: target.text,
        comment: draft.trim(),
      },
    ]);
    setTarget(null);
    setDraft("");
  };

  const copy = async () => {
    const text = asClipboard(notes);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Буфер закрыт политикой браузера — показываем текст, чтобы его можно
      // было выделить руками. Молча терять разметку нельзя.
      window.prompt("Скопируйте разметку:", text);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <>
      {on && hover !== null && target === null ? (
        <div
          className="pointer-events-none fixed z-[60] rounded-[3px] border border-accent bg-accent-dim/30"
          style={{
            top: hover.top,
            left: hover.left,
            width: hover.width,
            height: hover.height,
          }}
        />
      ) : null}

      <div ref={own}>
        {on && target !== null ? (
          <div
            className="fixed z-[70] w-[300px] rounded-[var(--radius-card)] border border-border-strong bg-bg p-2 shadow-lg"
            style={{
              top: Math.min(target.rect.bottom + 6, window.innerHeight - 150),
              left: Math.min(target.rect.left, window.innerWidth - 320),
            }}
          >
            <div
              className="truncate pb-1 text-[11px] text-fg-faint"
              title={target.anchor}
            >
              {target.anchor}
            </div>
            <textarea
              ref={field}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  add();
                }
              }}
              rows={3}
              placeholder="что не так с этим местом"
              className="w-full resize-none rounded-[var(--radius-ui)] border border-border bg-bg-subtle p-2 text-[12.5px] outline-none focus:border-border-strong"
            />
            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={add}
                className="h-7 rounded-[var(--radius-ui)] bg-accent px-2.5 text-[12px] font-medium text-bg"
              >
                добавить
              </button>
              <button
                type="button"
                onClick={() => setTarget(null)}
                className="h-7 rounded-[var(--radius-ui)] border border-border px-2.5 text-[12px] text-fg-muted"
              >
                отмена
              </button>
              <span className="ml-auto text-[10.5px] text-fg-faint">
                Enter — добавить
              </span>
            </div>
          </div>
        ) : null}

        <div className="fixed bottom-4 right-4 z-[70] flex flex-col items-end gap-2">
          {on && notes.length > 0 ? (
            <div className="max-h-[50vh] w-[330px] overflow-auto rounded-[var(--radius-card)] border border-border bg-bg p-2 shadow-lg">
              {notes.map((n, i) => (
                <div
                  key={n.id}
                  className="flex gap-2 border-b border-border py-1.5 last:border-0"
                >
                  <span className="tnum flex-none text-[11px] text-fg-faint">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div
                      className="truncate text-[10.5px] text-fg-faint"
                      title={n.anchor}
                    >
                      {n.anchor}
                    </div>
                    <div className="text-[12px]">{n.comment}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setNotes(notes.filter((x) => x.id !== n.id))}
                    className="flex-none self-start p-0.5 text-fg-faint hover:text-fg"
                    title="убрать"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
              <div className="flex items-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={copy}
                  className="flex h-7 items-center gap-1.5 rounded-[var(--radius-ui)] bg-accent px-2.5 text-[12px] font-medium text-bg"
                >
                  <Copy size={12} />
                  {copied ? "скопировано" : "скопировать всё"}
                </button>
                <button
                  type="button"
                  onClick={() => setNotes([])}
                  className="h-7 rounded-[var(--radius-ui)] border border-border px-2.5 text-[12px] text-fg-muted hover:text-fg"
                >
                  очистить
                </button>
              </div>
            </div>
          ) : null}

          <button
            type="button"
            onClick={() => {
              setOn(!on);
              setTarget(null);
            }}
            title={
              on
                ? "Выйти из разметки (Esc). Клик по элементу — комментарий."
                : "Разметка: клик по элементу экрана — комментарий, потом «скопировать всё» и вставить в сессию Claude"
            }
            className={
              "flex h-9 items-center gap-2 rounded-full border px-3 text-[12.5px] shadow-lg transition-colors " +
              (on
                ? "border-accent bg-accent font-medium text-bg"
                : "border-border bg-bg text-fg-muted hover:text-fg")
            }
          >
            {on ? <X size={14} /> : <MessageSquarePlus size={14} />}
            {on ? "разметка включена" : "разметка"}
            {notes.length > 0 ? (
              <span className="tnum rounded-full bg-bg-hover px-1.5 text-[11px] text-fg">
                {notes.length}
              </span>
            ) : null}
          </button>
        </div>
      </div>
    </>
  );
}
